import { payloadKeyHash } from "@/shared/hash/payload-key-hash";
import { stableStringify } from "@/shared/hash/stable-stringify";
import { roundMoney } from "@/domain/money/round-money";
import type { MenuCatalogItem } from "@/domain/cart/cart-validity.types";

type FingerprintModifierRow = {
  gid: string;
  mid: string;
  avail: boolean;
};

type FingerprintItemRow = {
  id: string;
  version: number;
  price: number;
  mods: FingerprintModifierRow[];
};

function compareModifierRows(a: FingerprintModifierRow, b: FingerprintModifierRow): number {
  const gid = a.gid.localeCompare(b.gid);
  if (gid !== 0) return gid;
  return a.mid.localeCompare(b.mid);
}

function toFingerprintItemRow(item: MenuCatalogItem): FingerprintItemRow {
  const mods: FingerprintModifierRow[] = [];
  for (const group of item.modifier_groups ?? []) {
    const gid = String(group.modifier_group_id ?? "").trim();
    for (const mod of group.items ?? []) {
      const mid = String(mod.modifier_group_item_id ?? "").trim();
      if (!gid || !mid) continue;
      mods.push({
        gid,
        mid,
        avail: mod.is_available !== false,
      });
    }
  }
  mods.sort(compareModifierRows);

  return {
    id: String(item.menu_item_id),
    version: Math.max(0, Math.floor(Number(item.menu_version) || 0)),
    price: roundMoney(item.item_price),
    mods,
  };
}

/**
 * Deterministic catalog fingerprint for validity drift detection.
 * Stable regardless of input array ordering.
 */
export function buildCatalogFingerprint(items: MenuCatalogItem[]): string {
  const rows = items
    .map(toFingerprintItemRow)
    .sort((a, b) => a.id.localeCompare(b.id));
  return payloadKeyHash(stableStringify(rows));
}
